import React, { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'

interface TeamMember {
  id: number
  first_name: string
  last_name: string
  title: string
  photo_url: string | null
  description: string | null
  linkedin_url: string | null
  twitter_url: string | null
  facebook_url: string | null
  instagram_url: string | null
  email: string | null
  display_order: number
}

// Social media icons as SVG
const SocialIcons = {
  linkedin: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
    </svg>
  ),
  twitter: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  ),
  facebook: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/>
    </svg>
  ),
  instagram: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
  ),
  email: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M0 3v18h24v-18h-24zm21.518 2l-9.518 7.713-9.518-7.713h19.036zm-19.518 14v-11.817l10 8.104 10-8.104v11.817h-20z"/>
    </svg>
  )
}

export const TeamSection: React.FC = () => {
  const [members, setMembers] = useState<TeamMember[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null)

  useEffect(() => {
    loadMembers()
  }, [])

  // Close modal on escape key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedMember(null)
    }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [])

  const loadMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('team_members')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true })

      if (error) throw error
      setMembers(data || [])
    } catch (error) {
      console.error('Error loading team members:', error)
    } finally {
      setLoading(false)
    }
  }

  const hasSocialLinks = (member: TeamMember) => {
    return member.linkedin_url || member.twitter_url || member.facebook_url || member.instagram_url || member.email
  }

  if (loading) {
    return (
      <section className="section" style={{ backgroundColor: '#050505', padding: '80px 0' }}>
        <div className="container" style={{ textAlign: 'center', color: '#ffffff' }}>
          Loading team...
        </div>
      </section>
    )
  }

  if (members.length === 0) {
    return null
  }

  return (
    <>
      <section className="section team-section" style={{
        backgroundColor: '#0A0A0A',
        padding: '80px 0',
        borderTop: '1px solid #1E1E1E',
        borderBottom: '1px solid #1E1E1E'
      }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#FFD700', marginBottom: '16px' }}>
              Our Leadership Team
            </h2>
            <p style={{ color: '#ffffff', fontSize: '18px', maxWidth: '600px', margin: '0 auto' }}>
              Meet the experienced professionals driving Aureus Alliance Holdings forward
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px',
            justifyItems: 'center'
          }}>
            {members.map(member => (
              <div
                key={member.id}
                onClick={() => setSelectedMember(member)}
                style={{
                  backgroundColor: '#121212',
                  borderRadius: '16px',
                  padding: '32px 24px',
                  textAlign: 'center',
                  border: '1px solid #1E1E1E',
                  transition: 'all 0.3s ease',
                  width: '100%',
                  maxWidth: '320px',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-4px)'
                  ;(e.currentTarget as HTMLDivElement).style.borderColor = '#FFD700'
                  ;(e.currentTarget as HTMLDivElement).style.boxShadow = '0 10px 30px rgba(255, 215, 0, 0.15)'
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)'
                  ;(e.currentTarget as HTMLDivElement).style.borderColor = '#1E1E1E'
                  ;(e.currentTarget as HTMLDivElement).style.boxShadow = 'none'
                }}
              >
                {member.photo_url ? (
                  <img
                    src={member.photo_url}
                    alt={`${member.first_name} ${member.last_name}`}
                    style={{
                      width: '140px',
                      height: '140px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '4px solid #FFD700',
                      marginBottom: '20px'
                    }}
                  />
                ) : (
                  <div style={{
                    width: '140px',
                    height: '140px',
                    borderRadius: '50%',
                    backgroundColor: '#1E1E1E',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '48px',
                    color: '#FFD700',
                    border: '4px solid #FFD700',
                    margin: '0 auto 20px'
                  }}>
                    {member.first_name[0]}{member.last_name[0]}
                  </div>
                )}
                <h3 style={{ fontSize: '22px', fontWeight: '600', color: '#FFFFFF', marginBottom: '8px' }}>
                  {member.first_name} {member.last_name}
                </h3>
                <p style={{ fontSize: '16px', color: '#FFD700', fontWeight: '500', marginBottom: '12px' }}>
                  {member.title}
                </p>
                <p style={{ fontSize: '13px', color: '#ffffff' }}>
                  Click to view profile →
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Slide-out Modal */}
      {selectedMember && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'flex-end',
            animation: 'fadeIn 0.3s ease'
          }}
          onClick={() => setSelectedMember(null)}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '500px',
              height: '100%',
              backgroundColor: '#0A0A0A',
              borderLeft: '1px solid #FFD700',
              overflowY: 'auto',
              animation: 'slideIn 0.3s ease',
              boxShadow: '-10px 0 40px rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setSelectedMember(null)}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                color: '#fff',
                fontSize: '24px',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}
            >
              ✕
            </button>

            {/* Header with photo */}
            <div style={{
              background: 'linear-gradient(180deg, #1a1a2e 0%, #0A0A0A 100%)',
              padding: '60px 32px 32px',
              textAlign: 'center',
              borderBottom: '1px solid #1E1E1E'
            }}>
              {selectedMember.photo_url ? (
                <img
                  src={selectedMember.photo_url}
                  alt={`${selectedMember.first_name} ${selectedMember.last_name}`}
                  style={{
                    width: '180px',
                    height: '180px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '5px solid #FFD700',
                    marginBottom: '24px',
                    boxShadow: '0 10px 40px rgba(255, 215, 0, 0.2)'
                  }}
                />
              ) : (
                <div style={{
                  width: '180px',
                  height: '180px',
                  borderRadius: '50%',
                  backgroundColor: '#1E1E1E',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '64px',
                  color: '#FFD700',
                  border: '5px solid #FFD700',
                  margin: '0 auto 24px',
                  boxShadow: '0 10px 40px rgba(255, 215, 0, 0.2)'
                }}>
                  {selectedMember.first_name[0]}{selectedMember.last_name[0]}
                </div>
              )}
              <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#FFFFFF', marginBottom: '8px' }}>
                {selectedMember.first_name} {selectedMember.last_name}
              </h2>
              <p style={{ fontSize: '18px', color: '#FFD700', fontWeight: '500' }}>
                {selectedMember.title}
              </p>
            </div>

            {/* Content */}
            <div style={{ padding: '32px' }}>
              {selectedMember.description && (
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ fontSize: '14px', color: '#ffffff', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    About
                  </h3>
                  <p style={{ color: '#E5E7EB', fontSize: '16px', lineHeight: '1.7' }}>
                    {selectedMember.description}
                  </p>
                </div>
              )}

              {hasSocialLinks(selectedMember) && (
                <div>
                  <h3 style={{ fontSize: '14px', color: '#ffffff', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    Connect
                  </h3>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    {selectedMember.linkedin_url && (
                      <a
                        href={selectedMember.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px 16px',
                          backgroundColor: '#0077B5',
                          color: '#fff',
                          borderRadius: '8px',
                          textDecoration: 'none',
                          fontSize: '14px',
                          fontWeight: '500',
                          transition: 'all 0.2s'
                        }}
                      >
                        {SocialIcons.linkedin} LinkedIn
                      </a>
                    )}
                    {selectedMember.twitter_url && (
                      <a
                        href={selectedMember.twitter_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px 16px',
                          backgroundColor: '#1DA1F2',
                          color: '#fff',
                          borderRadius: '8px',
                          textDecoration: 'none',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                      >
                        {SocialIcons.twitter} Twitter / X
                      </a>
                    )}
                    {selectedMember.facebook_url && (
                      <a
                        href={selectedMember.facebook_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px 16px',
                          backgroundColor: '#1877F2',
                          color: '#fff',
                          borderRadius: '8px',
                          textDecoration: 'none',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                      >
                        {SocialIcons.facebook} Facebook
                      </a>
                    )}
                    {selectedMember.instagram_url && (
                      <a
                        href={selectedMember.instagram_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px 16px',
                          background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                          color: '#fff',
                          borderRadius: '8px',
                          textDecoration: 'none',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                      >
                        {SocialIcons.instagram} Instagram
                      </a>
                    )}
                    {selectedMember.email && (
                      <a
                        href={`mailto:${selectedMember.email}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px 16px',
                          backgroundColor: '#FFD700',
                          color: '#000',
                          borderRadius: '8px',
                          textDecoration: 'none',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                      >
                        {SocialIcons.email} Email
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  )
}

export default TeamSection

